import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useReactTable, getCoreRowModel, getSortedRowModel,
  getPaginationRowModel, getFilteredRowModel, flexRender,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ArrowUp, ArrowDown, ChevronsUpDown, ChevronLeft, ChevronRight, Download } from "lucide-react";
import ColumnVisibilityMenu from "./ColumnVisibilityMenu";

/**
 * DataTable — enterprise data table: sorting, column visibility, resizing,
 * sticky header, pagination, search, row selection, export-selected.
 * Operates client-side over `data` (the full filtered row set); rows beyond
 * the current page are virtualized so large sets stay fast.
 *
 * Built with CSS Grid (not a real <table>) for both the header and body
 * rows, driven by one shared `gridTemplateColumns` string — a semantic
 * <table> lays out <thead> (table layout algorithm) and a virtualized,
 * absolutely-positioned <tbody> (plain flow) with two DIFFERENT width
 * systems, which drift apart whenever the column count changes (e.g.
 * toggling a column off) and the header stretches to fill 100% while the
 * body cells stay at their literal pixel width. Grid keeps both in lockstep
 * because they read the exact same track list.
 *
 * Props:
 *  columns: [{ id, label, accessor?: (row) => value, width?: number }]
 *  data: object[]
 *  theme, isRTL
 *  labels: { search, columns, showingRows(from,to,total), rowsPerPage, noResults, selected(count), exportSelected }
 *  onExportSelected: (selectedRows) => void
 *  onVisibleColumnsChange: (visibleColumnIds: string[]) => void — fires whenever
 *    a column is hidden/shown via the Columns menu, so a parent (e.g. the
 *    report's PDF/Excel export) can restrict its own output to match what's
 *    actually showing on screen.
 *  pageSize: number (default 25)
 */
const DataTable = ({
  columns,
  data = [],
  theme = "light",
  isRTL = false,
  labels = {},
  onExportSelected,
  onVisibleColumnsChange,
  pageSize = 25,
}) => {
  const isDark = theme === "dark";
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({});
  const [rowSelection, setRowSelection] = useState({});
  const [colWidths, setColWidths] = useState({});
  const resizingRef = useRef(null);

  const tableColumns = useMemo(() => [
    {
      id: "__select",
      header: ({ table }) => (
        <input
          type="checkbox"
          aria-label={labels.selectAll || "Select all"}
          checked={table.getIsAllRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <input type="checkbox" checked={row.getIsSelected()} onChange={row.getToggleSelectedHandler()} aria-label="Select row" />
      ),
      enableSorting: false,
      size: 36,
    },
    ...columns.map((col) => ({
      id: col.id,
      accessorFn: col.accessor || ((row) => row[col.id]),
      header: col.label,
      size: colWidths[col.id] || col.width || 160,
      cell: (info) => {
        const v = info.getValue();
        return v === null || v === undefined || v === "" ? "—" : String(v);
      },
    })),
  ], [columns, colWidths, labels.selectAll]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: { sorting, globalFilter, columnVisibility, rowSelection },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
  });

  const { rows } = table.getRowModel();
  const parentRef = useRef(null);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 42,
    overscan: 8,
  });

  // Single source of truth for column widths, shared verbatim by the header
  // row and every body row so they can never drift apart.
  const visibleLeafColumns = table.getVisibleLeafColumns();
  const gridTemplateColumns = visibleLeafColumns.map((col) => `${col.getSize()}px`).join(" ");

  useEffect(() => {
    onVisibleColumnsChange?.(visibleLeafColumns.map((c) => c.id).filter((id) => id !== "__select"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnVisibility, columns]);

  const startResize = (colId) => (e) => {
    e.preventDefault();
    resizingRef.current = { colId, startX: e.clientX, startWidth: colWidths[colId] || 160 };
    const onMove = (me) => {
      if (!resizingRef.current) return;
      const delta = me.clientX - resizingRef.current.startX;
      const next = Math.max(80, resizingRef.current.startWidth + (isRTL ? -delta : delta));
      setColWidths((w) => ({ ...w, [resizingRef.current.colId]: next }));
    };
    const onUp = () => {
      resizingRef.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // Export Selected must mirror whatever columns are actually showing —
  // a hidden column shouldn't reappear in the exported file just because
  // it's still present on the underlying row object.
  const visibleColumnIds = visibleLeafColumns.map((c) => c.id).filter((id) => id !== "__select");
  const selectedRows = table.getSelectedRowModel().rows.map((r) => {
    const picked = {};
    visibleColumnIds.forEach((id) => { picked[id] = r.original[id]; });
    return picked;
  });
  const pageIndex = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();

  const border = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
  const headerBg = isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.03)";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Toolbar */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ position: "relative", flex: "1 1 220px", maxWidth: 320 }}>
          {/* <Search size={14} style={{ position: "absolute", top: "50%", [isRTL ? "right" : "left"]: 10, transform: "translateY(-50%)", opacity: 0.5 }} /> */}
          <input
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder={labels.search || "Search table..."}
            style={{
              width: "100%", padding: `8px ${isRTL ? "10px" : "32px"} 8px ${isRTL ? "32px" : "10px"}`,
              borderRadius: 10, border: `1px solid ${border}`,
              background: isDark ? "rgba(255,255,255,0.05)" : "#fff",
              color: isDark ? "#f1f5f9" : "#1e293b", fontSize: 13,
            }}
          />
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {selectedRows.length > 0 && (
            <>
              {/* <span style={{ fontSize: 12.5, fontWeight: 600, color: isDark ? "#93c5fd" : "#1d4ed8" }}>
                {(labels.selected || "{count} selected").replace("{count}", selectedRows.length)}
              </span> */}
              <div style={{ display: "flex", borderRadius: 10, overflow: "hidden" }}>
                <button
                  type="button"
                  onClick={() => onExportSelected?.(selectedRows, "xlsx")}
                  style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "8px 12px",
                    border: "none", background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff",
                    fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                  }}
                  title={labels.exportSelected || "Export Selected"}
                >
                  <Download size={14} /> {labels.exportSelectedExcel || "Excel"}
                </button>
                <button
                  type="button"
                  onClick={() => onExportSelected?.(selectedRows, "pdf")}
                  style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "8px 12px",
                    border: "none", borderInlineStart: "1px solid rgba(255,255,255,0.25)",
                    background: "linear-gradient(135deg, #2563eb, #1e3a8a)", color: "#fff",
                    fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                  }}
                  title={labels.exportSelected || "Export Selected"}
                >
                  <Download size={14} /> {labels.exportSelectedPdf || "PDF"}
                </button>
              </div>
            </>
          )}
          <ColumnVisibilityMenu
            columns={columns}
            columnVisibility={columnVisibility}
            onToggle={(id) => setColumnVisibility((v) => ({ ...v, [id]: v[id] === false ? true : false }))}
            theme={theme}
            isRTL={isRTL}
            label={labels.columns || "Columns"}
          />
        </div>
      </div>

      {/* Table (CSS Grid, not a real <table> — see the note on the component above) */}
      <div
        ref={parentRef}
        role="table"
        className="data-table-scroll"
        style={{
          maxHeight: 480, overflow: "auto", borderRadius: 14,
          border: `1px solid ${border}`,
        }}
      >
        <div role="rowgroup" style={{ position: "sticky", top: 0, zIndex: 5 }}>
          <div
            role="row"
            style={{
              display: "grid", gridTemplateColumns, minWidth: "fit-content",
              background: headerBg, backdropFilter: "blur(8px)", borderBottom: `1px solid ${border}`,
            }}
          >
            {table.getHeaderGroups().map((hg) =>
              hg.headers.map((header) => (
                <div
                  key={header.id}
                  role="columnheader"
                  style={{
                    position: "relative", padding: "10px 12px",
                    textAlign: isRTL ? "right" : "left", fontSize: 11.5, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: 0.4,
                    color: isDark ? "rgba(255,255,255,0.75)" : "rgba(30,41,59,0.7)",
                    cursor: header.column.getCanSort() ? "pointer" : "default",
                    userSelect: "none", overflow: "hidden",
                  }}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      header.column.getIsSorted() === "asc" ? <ArrowUp size={12} /> :
                      header.column.getIsSorted() === "desc" ? <ArrowDown size={12} /> :
                      <ChevronsUpDown size={12} style={{ opacity: 0.35 }} />
                    )}
                  </div>
                  {header.id !== "__select" && (
                    <div
                      onMouseDown={startResize(header.column.id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: "absolute", top: 0, [isRTL ? "left" : "right"]: 0, height: "100%", width: 6,
                        cursor: "col-resize",
                      }}
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div role="rowgroup" style={{ position: "relative", height: rows.length === 0 ? "auto" : rowVirtualizer.getTotalSize(), minWidth: "fit-content" }}>
          {rows.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: isDark ? "rgba(255,255,255,0.4)" : "rgba(30,41,59,0.4)", fontSize: 13 }}>
              {labels.noResults || "No matching rows"}
            </div>
          ) : rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index];
            return (
              <div
                key={row.id}
                role="row"
                style={{
                  position: "absolute", top: 0, left: 0, right: 0,
                  transform: `translateY(${virtualRow.start}px)`,
                  display: "grid", gridTemplateColumns,
                  background: row.getIsSelected() ? (isDark ? "rgba(59,130,246,0.12)" : "rgba(37,99,235,0.06)") : "transparent",
                  borderBottom: `1px solid ${border}`,
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <div
                    key={cell.id}
                    role="cell"
                    style={{
                      padding: "9px 12px", fontSize: 12.5,
                      color: isDark ? "#e2e8f0" : "#334155",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      display: "flex", alignItems: "center",
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: isRTL ? "flex-start" : "flex-end", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexDirection: isRTL ? "row-reverse" : "row" }}>
          <button
            type="button"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            aria-label="Previous page"
            style={pagerBtnStyle(isDark, !table.getCanPreviousPage())}
          >
            {isRTL ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
          </button>
          <span style={{ fontSize: 12.5, color: isDark ? "#e2e8f0" : "#334155", minWidth: 70, textAlign: "center" }}>
            {(labels.page || "Page")} {pageIndex + 1} {(labels.of || "of")} {Math.max(1, pageCount)}
          </span>
          <button
            type="button"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            aria-label="Next page"
            style={pagerBtnStyle(isDark, !table.getCanNextPage())}
          >
            {isRTL ? <ChevronLeft size={15} /> : <ChevronRight size={15} />}
          </button>
        </div>
      </div>

      <style>{`
        .data-table-scroll {
          scrollbar-width: thin;
          scrollbar-color: ${isDark ? "rgba(148, 163, 184, 0.55) rgba(15, 23, 42, 0.85)" : "rgba(148, 163, 184, 0.45) rgba(255, 255, 255, 0.95)"};
        }
        .data-table-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
        .data-table-scroll::-webkit-scrollbar-track { background: ${isDark ? "rgba(148, 163, 184, 0.08)" : "rgba(226, 232, 240, 0.75)"}; }
        .data-table-scroll::-webkit-scrollbar-thumb {
          background: ${isDark ? "rgba(148, 163, 184, 0.5)" : "rgba(148, 163, 184, 0.55)"};
          border-radius: 999px; border: 2px solid transparent; background-clip: content-box;
        }
        .data-table-scroll::-webkit-scrollbar-thumb:hover { background: ${isDark ? "rgba(148, 163, 184, 0.75)" : "rgba(148, 163, 184, 0.75)"}; }
        .data-table-scroll::-webkit-scrollbar-corner { background: transparent; }
      `}</style>
    </div>
  );
};

const pagerBtnStyle = (isDark, disabled) => ({
  display: "flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8,
  border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"}`,
  background: isDark ? "rgba(255,255,255,0.05)" : "#fff",
  color: isDark ? "#e2e8f0" : "#334155",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.4 : 1,
});

export default DataTable;
